import type { Organization } from "../providers.types";
import {
  getOrganizationCountryLabel,
  normalizeCountryCode,
} from "../countryMetadata";
import usOrganizationsData from "./organizations.json";

type OrganizationDataModule = {
  default: unknown;
};

type DatasetKey =
  | "AE"
  | "AF"
  | "AO"
  | "AU"
  | "BB"
  | "BD"
  | "BE"
  | "BF"
  | "BH"
  | "BJ"
  | "BS"
  | "BW"
  | "CA"
  | "CI"
  | "CM"
  | "CN"
  | "DE"
  | "DZ"
  | "EG"
  | "ES"
  | "ET"
  | "EUROPE"
  | "FR"
  | "GA"
  | "GB"
  | "GH"
  | "GR"
  | "HK"
  | "HN"
  | "ID"
  | "IL"
  | "IN"
  | "IQ"
  | "IR"
  | "IT"
  | "JM"
  | "JO"
  | "JP"
  | "KE"
  | "KH"
  | "KR"
  | "KW"
  | "KY"
  | "KZ"
  | "LA"
  | "LATAM"
  | "LB"
  | "LK"
  | "LY"
  | "MA"
  | "MD"
  | "MG"
  | "MM"
  | "MN"
  | "MU"
  | "MV"
  | "MX"
  | "MY"
  | "MZ"
  | "NA"
  | "NG"
  | "NI"
  | "NP"
  | "NZ"
  | "OM"
  | "PG"
  | "PH"
  | "PK"
  | "PS"
  | "PT"
  | "QA"
  | "RU"
  | "RW"
  | "SA"
  | "SD"
  | "SG"
  | "SN"
  | "SY"
  | "TG"
  | "TH"
  | "TN"
  | "TR"
  | "TT"
  | "TW"
  | "TZ"
  | "UG"
  | "US"
  | "UZ"
  | "VN"
  | "XK"
  | "YE"
  | "ZA"
  | "ZM"
  | "ZW";

export type OrganizationCountryOption = {
  value: string;
  label: string;
};

const DATASET_COUNTRIES: Record<DatasetKey, readonly string[]> = {
  AE: ["AE"],
  AF: ["AF"],
  AO: ["AO"],
  AU: ["AU"],
  BB: ["BB"],
  BD: ["BD"],
  BE: ["BE"],
  BF: ["BF"],
  BH: ["BH"],
  BJ: ["BJ"],
  BS: ["BS"],
  BW: ["BW"],
  CA: ["CA"],
  CI: ["CI"],
  CM: ["CM"],
  CN: ["CN"],
  DE: ["DE"],
  DZ: ["DZ"],
  EG: ["EG"],
  ES: ["ES"],
  ET: ["ET"],
  EUROPE: [
    "AL",
    "AM",
    "AT",
    "AZ",
    "BA",
    "BG",
    "BY",
    "CH",
    "CY",
    "CZ",
    "DK",
    "EE",
    "FI",
    "GE",
    "HR",
    "HU",
    "IE",
    "IS",
    "LT",
    "LU",
    "LV",
    "ME",
    "MK",
    "MT",
    "NL",
    "NO",
    "PL",
    "RO",
    "RS",
    "SE",
    "SI",
    "SK",
    "UA",
  ],
  FR: ["FR"],
  GA: ["GA"],
  GB: ["GB"],
  GH: ["GH"],
  GR: ["GR"],
  HK: ["HK"],
  HN: ["HN"],
  ID: ["ID"],
  IL: ["IL"],
  IN: ["IN"],
  IQ: ["IQ"],
  IR: ["IR"],
  IT: ["IT"],
  JM: ["JM"],
  JO: ["JO"],
  JP: ["JP"],
  KE: ["KE"],
  KH: ["KH"],
  KR: ["KR"],
  KW: ["KW"],
  KY: ["KY"],
  KZ: ["KZ"],
  LA: ["LA"],
  LATAM: [
    "AR",
    "BO",
    "BR",
    "CL",
    "CO",
    "CR",
    "DO",
    "EC",
    "GT",
    "PA",
    "PE",
    "PY",
    "SV",
    "UY",
    "VE",
  ],
  LB: ["LB"],
  LK: ["LK"],
  LY: ["LY"],
  MA: ["MA"],
  MD: ["MD"],
  MG: ["MG"],
  MM: ["MM"],
  MN: ["MN"],
  MU: ["MU"],
  MV: ["MV"],
  MX: ["MX"],
  MY: ["MY"],
  MZ: ["MZ"],
  NA: ["NA"],
  NG: ["NG"],
  NI: ["NI"],
  NP: ["NP"],
  NZ: ["NZ"],
  OM: ["OM"],
  PG: ["PG"],
  PH: ["PH"],
  PK: ["PK"],
  PS: ["PS"],
  PT: ["PT"],
  QA: ["QA"],
  RU: ["RU"],
  RW: ["RW"],
  SA: ["SA"],
  SD: ["SD"],
  SG: ["SG"],
  SN: ["SN"],
  SY: ["SY"],
  TG: ["TG"],
  TH: ["TH"],
  TN: ["TN"],
  TR: ["TR"],
  TT: ["TT"],
  TW: ["TW"],
  TZ: ["TZ"],
  UG: ["UG"],
  US: ["US"],
  UZ: ["UZ"],
  VN: ["VN"],
  XK: ["XK"],
  YE: ["YE"],
  ZA: ["ZA"],
  ZM: ["ZM"],
  ZW: ["ZW"],
};

const DATASET_LOADERS: Record<
  Exclude<DatasetKey, "US">,
  () => Promise<unknown>
> = {
  AE: () => import("./ae-organizations.json"),
  AF: () => import("./af-organizations.json"),
  AO: () => import("./ao-organizations.json"),
  AU: () => import("./au-organizations.json"),
  BB: () => import("./bb-organizations.json"),
  BD: () => import("./bd-organizations.json"),
  BE: () => import("./be-organizations.json"),
  BF: () => import("./bf-organizations.json"),
  BH: () => import("./bh-organizations.json"),
  BJ: () => import("./bj-organizations.json"),
  BS: () => import("./bs-organizations.json"),
  BW: () => import("./bw-organizations.json"),
  CA: () => import("./ca-organizations.json"),
  CI: () => import("./ci-organizations.json"),
  CM: () => import("./cm-organizations.json"),
  CN: () => import("./cn-organizations.json"),
  DE: () => import("./de-organizations.json"),
  DZ: () => import("./dz-organizations.json"),
  EG: () => import("./eg-organizations.json"),
  ES: () => import("./es-organizations.json"),
  ET: () => import("./et-organizations.json"),
  EUROPE: () => import("./europe-organizations.json"),
  FR: () => import("./fr-organizations.json"),
  GA: () => import("./ga-organizations.json"),
  GB: () => import("./gb-organizations.json"),
  GH: () => import("./gh-organizations.json"),
  GR: () => import("./gr-organizations.json"),
  HK: () => import("./hk-organizations.json"),
  HN: () => import("./hn-organizations.json"),
  ID: () => import("./id-organizations.json"),
  IL: () => import("./il-organizations.json"),
  IN: () => import("./in-organizations.json"),
  IQ: () => import("./iq-organizations.json"),
  IR: () => import("./ir-organizations.json"),
  IT: () => import("./it-organizations.json"),
  JM: () => import("./jm-organizations.json"),
  JO: () => import("./jo-organizations.json"),
  JP: () => import("./jp-organizations.json"),
  KE: () => import("./ke-organizations.json"),
  KH: () => import("./kh-organizations.json"),
  KR: () => import("./kr-organizations.json"),
  KW: () => import("./kw-organizations.json"),
  KY: () => import("./ky-organizations.json"),
  KZ: () => import("./kz-organizations.json"),
  LA: () => import("./la-organizations.json"),
  LATAM: () => import("./latam-organizations.json"),
  LB: () => import("./lb-organizations.json"),
  LK: () => import("./lk-organizations.json"),
  LY: () => import("./ly-organizations.json"),
  MA: () => import("./ma-organizations.json"),
  MD: () => import("./md-organizations.json"),
  MG: () => import("./mg-organizations.json"),
  MM: () => import("./mm-organizations.json"),
  MN: () => import("./mn-organizations.json"),
  MU: () => import("./mu-organizations.json"),
  MV: () => import("./mv-organizations.json"),
  MX: () => import("./mx-organizations.json"),
  MY: () => import("./my-organizations.json"),
  MZ: () => import("./mz-organizations.json"),
  NA: () => import("./na-organizations.json"),
  NG: () => import("./ng-organizations.json"),
  NI: () => import("./ni-organizations.json"),
  NP: () => import("./np-organizations.json"),
  NZ: () => import("./nz-organizations.json"),
  OM: () => import("./om-organizations.json"),
  PG: () => import("./pg-organizations.json"),
  PH: () => import("./ph-organizations.json"),
  PK: () => import("./pk-organizations.json"),
  PS: () => import("./ps-organizations.json"),
  PT: () => import("./pt-organizations.json"),
  QA: () => import("./qa-organizations.json"),
  RU: () => import("./ru-organizations.json"),
  RW: () => import("./rw-organizations.json"),
  SA: () => import("./sa-organizations.json"),
  SD: () => import("./sd-organizations.json"),
  SG: () => import("./sg-organizations.json"),
  SN: () => import("./sn-organizations.json"),
  SY: () => import("./sy-organizations.json"),
  TG: () => import("./tg-organizations.json"),
  TH: () => import("./th-organizations.json"),
  TN: () => import("./tn-organizations.json"),
  TR: () => import("./tr-organizations.json"),
  TT: () => import("./tt-organizations.json"),
  TW: () => import("./tw-organizations.json"),
  TZ: () => import("./tz-organizations.json"),
  UG: () => import("./ug-organizations.json"),
  UZ: () => import("./uz-organizations.json"),
  VN: () => import("./vn-organizations.json"),
  XK: () => import("./xk-organizations.json"),
  YE: () => import("./ye-organizations.json"),
  ZA: () => import("./za-organizations.json"),
  ZM: () => import("./zm-organizations.json"),
  ZW: () => import("./zw-organizations.json"),
};

const COUNTRY_TO_DATASET = Object.entries(DATASET_COUNTRIES).reduce<
  Record<string, DatasetKey>
>((acc, [datasetKey, countries]) => {
  for (const country of countries) {
    acc[country] = datasetKey as DatasetKey;
  }
  return acc;
}, {});

const loadedDatasets = new Map<DatasetKey, Organization[]>([
  ["US", usOrganizationsData as unknown as Organization[]],
]);
const pendingDatasets = new Map<DatasetKey, Promise<Organization[]>>();
const ALL_DATASET_KEYS = Object.keys(DATASET_COUNTRIES) as DatasetKey[];

function getModuleData(
  module: OrganizationDataModule | unknown,
): Organization[] {
  const value =
    module != null && typeof module === "object" && "default" in module
      ? (module as OrganizationDataModule).default
      : module;
  return value as Organization[];
}

export function getDatasetKeysForCountries(
  countries: string[] | undefined,
): DatasetKey[] {
  if (!countries || countries.length === 0) return ALL_DATASET_KEYS;
  if (countries.includes("__none__")) return [];

  return Array.from(
    new Set(
      countries
        .map((country) => COUNTRY_TO_DATASET[normalizeCountryCode(country)])
        .filter((dataset): dataset is DatasetKey => Boolean(dataset)),
    ),
  );
}

export function getDatasetKeysForOrganizationId(
  organizationId: string,
): DatasetKey[] {
  if (organizationId.startsWith("org-abm-")) return ["FR"];
  if (organizationId.startsWith("org-cdc-")) return ["US"];
  if (organizationId.startsWith("org-web-")) return ["US"];
  if (organizationId.startsWith("org-latam-")) return ["LATAM"];

  const match = organizationId.match(/^org-([a-z]{2})-/i);
  const dataset = match?.[1]
    ? COUNTRY_TO_DATASET[normalizeCountryCode(match[1])]
    : undefined;
  return dataset ? [dataset] : ["US"];
}

export function getLoadedOrganizationsForDatasetKeys(
  datasetKeys: DatasetKey[],
): Organization[] {
  return datasetKeys.flatMap(
    (datasetKey) => loadedDatasets.get(datasetKey) ?? [],
  );
}

export function areOrganizationDatasetsLoaded(datasetKeys: DatasetKey[]) {
  return datasetKeys.every((datasetKey) => loadedDatasets.has(datasetKey));
}

export function loadOrganizationDatasets(
  datasetKeys: DatasetKey[],
): Promise<Organization[]> {
  const loads = datasetKeys.map((datasetKey) => {
    const loaded = loadedDatasets.get(datasetKey);
    if (loaded) return Promise.resolve(loaded);

    const pending = pendingDatasets.get(datasetKey);
    if (pending) return pending;

    const loader = datasetKey === "US" ? null : DATASET_LOADERS[datasetKey];
    if (!loader) return Promise.resolve([]);

    const nextLoad = loader()
      .then(getModuleData)
      .then((data) => {
        loadedDatasets.set(datasetKey, data);
        pendingDatasets.delete(datasetKey);
        return data;
      })
      .catch((error) => {
        pendingDatasets.delete(datasetKey);
        throw error;
      });

    pendingDatasets.set(datasetKey, nextLoad);
    return nextLoad;
  });

  return Promise.all(loads).then((datasets) => datasets.flat());
}

export const AVAILABLE_ORGANIZATION_COUNTRIES = Object.freeze(
  Array.from(new Set(Object.values(DATASET_COUNTRIES).flat())).sort((a, b) =>
    getOrganizationCountryLabel(a).localeCompare(
      getOrganizationCountryLabel(b),
    ),
  ),
);

export const ORGANIZATION_COUNTRY_OPTIONS: OrganizationCountryOption[] =
  AVAILABLE_ORGANIZATION_COUNTRIES.map((country) => ({
    value: country,
    label: getOrganizationCountryLabel(country),
  }));

export { getOrganizationCountryLabel };
